<template>
  <div class="grid grid-rows-[auto_auto_1fr] h-full mission-home-page">
    <!-- 顶部导航栏 -->
    <ThreadsNav :show-center="false" :show-back="true" />

    <div
      class="w-full overflow-hidden bg-[url('/img/new-mission-bg.png')] py-5 bg-cover bg-center"
    >
      <div class="red-dot ml-4 mb-2">新的外派</div>

      <div
        :class="[
          'w-full overflow-x-auto px-4 flex gap-3',
          isMobile() ? 'new-mission-card' : '',
        ]"
      >
        <button
          class="relative bg-base-100 hover:bg-base-200 border border-base-300 hover:border-primary/20 w-[280px] min-w-[280px] sm:w-[320px] sm:min-w-[320px] p-4 rounded-xl flex gap-3 items-center transition-all duration-300 ease-in-out shadow-sm hover:shadow-md active:scale-[0.98]"
          @click="clickCreateMission(DispatchType.Mission)"
        >
          <div
            class="flex flex-col gap-1 items-center justify-center w-16 h-16 rounded-lg bg-primary/10 group-hover:bg-primary/15 transition-colors duration-300"
          >
            <i
              class="iconify mingcute--planet-line text-xl text-primary group-hover:text-primary/80"
            />
            <p class="text-sm font-medium text-base-content">任务</p>
          </div>

          <div class="flex flex-col gap-2 flex-1 text-left min-w-0">
            <p
              class="text-base font-semibold text-base-content group-hover:text-primary transition-colors duration-300"
            >
              挑战任务，铸就传说
            </p>
            <p class="text-xs text-base-content/70 leading-relaxed">
              派遣你的OC完成委托任务，不仅能收获珍贵的手帐和各式独特的装备，还能在活跃榜留下你的传说。
            </p>
          </div>

          <!-- 装饰性角标 -->
          <div
            class="absolute top-2 right-2 w-2 h-2 bg-primary rounded-full opacity-60 group-hover:opacity-100 transition-opacity duration-300"
          ></div>
        </button>

        <button
          class="relative bg-base-100 hover:bg-base-200 border border-base-300 hover:border-secondary/20 w-[280px] min-w-[280px] sm:w-[320px] sm:min-w-[320px] p-4 rounded-xl flex gap-3 items-center transition-all duration-300 ease-in-out shadow-sm hover:shadow-md active:scale-[0.98]"
          @click="clickCreateMission(DispatchType.Stroll)"
        >
          <div
            class="flex flex-col gap-1 items-center justify-center w-16 h-16 rounded-lg bg-secondary/10 group-hover:bg-secondary/15 transition-colors duration-300"
          >
            <i
              class="iconify mingcute--walk-line text-xl text-secondary group-hover:text-secondary/80"
            />
            <p class="text-sm font-medium text-base-content">溜达</p>
          </div>

          <div class="flex flex-col gap-2 flex-1 text-left min-w-0">
            <p
              class="text-base font-semibold text-base-content group-hover:text-secondary transition-colors duration-300"
            >
              奇遇漫步，邂逅新友
            </p>
            <p class="text-xs text-base-content/70 leading-relaxed">
              让你的OC开启一场悠闲的漫步吧！TA会带回记录着奇闻趣事的专属手帐。途中可能会邂逅其他有趣的灵魂哦。
            </p>
          </div>

          <!-- 装饰性角标 -->
          <div
            class="absolute top-2 right-2 w-2 h-2 bg-secondary rounded-full opacity-60 group-hover:opacity-100 transition-opacity duration-300"
          ></div>
        </button>
      </div>
    </div>

    <!-- 分类卡片 -->
    <VanTabs
      swipeable
      v-model:active="missionStore.activeTab"
      @change="onTabChange"
      class="overflow-hidden"
    >
      <!-- 标签栏 -->
      <VanTab
        v-for="item in tabList"
        :key="item"
        :name="item"
        :title="$t(`mission.dispatchRequestStatusMap.${item}`)"
        :badge="
          item === MateDispatchStatusRequest.Returned
            ? notificationStore.interactionSummary
                ?.awaitingReceptionDispatchCount ?? 0
            : undefined
        "
        :show-zero-badge="false"
      >
        <ThreadList
          :ref="
            (el) => {
              if (el) threadListRefs[item] = el as unknown as ThreadListInstance;
            }
          "
          :on-pull-load="() => reload()"
          :on-scroll-load="() => loadNextPageData()"
          @on-scroll="heightMap[item] = $event"
        >
          <div class="pt-1 flex flex-col gap-1">
            <MissionListItem
              v-for="mateDispatchId in missionStore.listMap[item]"
              :key="mateDispatchId"
              :item="missionStore.getDispatchById(mateDispatchId)"
            />
          </div>
        </ThreadList>
      </VanTab>
    </VanTabs>
  </div>
</template>

<script setup lang="ts">
import { ApiGetMateDispatchPage } from "@/apis/dispatchMission";
import { createPrivateSession } from "@/components/mobile/chat/chatUtils";
import { ThreadListInstance } from "@/components/mobile/ThreadList.vue";
import {
  DispatchStatus,
  DispatchType,
  MateDispatchStatusRequest,
  MateInteractionStatus,
} from "@/enums/mission";
import { isMobile } from "@/modules/common";
import { useMate } from "@/stores/mate";
import { useMission } from "@/stores/mission";
import { useNotification } from "@/stores/notification";
import {
  sessionStorageGet,
  sessionStorageRemove,
} from "@/tools/sessionStorage";
import { MateDispatch } from "@/types/entity";
import moment from "moment-timezone";
import { onActivated, onDeactivated, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

const missionStore = useMission();
const notificationStore = useNotification();
const router = useRouter();
const { t } = useI18n();

const tabList = ref([
  "all", // all
  MateDispatchStatusRequest.Preparing,
  MateDispatchStatusRequest.Dispatched,
  MateDispatchStatusRequest.Returned,
  MateDispatchStatusRequest.Settled,
  MateDispatchStatusRequest.Canceled,
]);

const heightMap = ref<Record<string, number>>({
  all: 0,
  [MateDispatchStatusRequest.Preparing]: 0,
  [MateDispatchStatusRequest.Dispatched]: 0,
  [MateDispatchStatusRequest.Returned]: 0,
  [MateDispatchStatusRequest.Settled]: 0,
  [MateDispatchStatusRequest.Canceled]: 0,
});
const countdownMap = ref<Record<number, string>>({});

const threadListRefs = ref<Record<string, ThreadListInstance>>({});

const isRunning = (dispatch: MateDispatch) => {
  return [
    DispatchStatus.Dispatched,
    DispatchStatus.ReturningWithLoots,
  ].includes(dispatch?.status as DispatchStatus);
};

const clickCreateMission = (type: DispatchType) => {
  router.push({
    name: "missionEdit",
    query: {
      type,
    },
  });
};

let timer: NodeJS.Timeout | null = null;

const reload = async () => {
  const list = missionStore.listMap[missionStore.activeTab];
  list.length = 0;

  heightMap.value[missionStore.activeTab] = 0;
  const threadList = threadListRefs.value[missionStore.activeTab];

  if (threadList) {
    threadList.resetScrollLoadingState();
  }
};

const loadNextPageData = async () => {
  const list = missionStore.listMap[missionStore.activeTab];
  const { length } = list;
  const page = length / 20 + 1;
  if (length !== 0 && length % 20 !== 0) return false;

  let status: MateDispatchStatusRequest | undefined;

  if (missionStore.activeTab !== "all") {
    status = missionStore.activeTab as MateDispatchStatusRequest;
  }

  const res = await ApiGetMateDispatchPage({
    filter: {
      status,
      mateId: useMate().userMate?.id,
    },
    pagination: { page, perPage: 20 },
    sort: { field: "dtUpdate", order: "desc" },
  });
  if (page === 1) list.length = 0;

  list.push(
    ...res.page.data.map((item) => {
      missionStore.mateDispatchMap[item.id] = item;
      return item.id;
    })
  );

  if (list.length > 0) {
    countdownMap.value = {};
    list.forEach((id) => {
      const item = missionStore.getDispatchById(id);
      if (isRunning(item) && !countdownMap.value[id]) {
        countdownMap.value[id] = "";
      }
    });
    initCountdown();
  }

  return res.page.data.length === 20;
};

const initCountdown = () => {
  const now = moment();
  Object.keys(countdownMap.value).forEach((key) => {
    const mateDispatch = missionStore.mateDispatchMap[Number(key)];
    if (mateDispatch) {
      const endTime = moment(mateDispatch.dtReturn);
      const allSeconds = endTime.diff(now, "seconds");
      const minute = String(Math.floor(allSeconds / 60)).padStart(2, "0");
      const second = String(allSeconds % 60).padStart(2, "0");
      if (allSeconds <= 0) {
        delete countdownMap.value[Number(key)];
        mateDispatch.interactionStatus =
          MateInteractionStatus.DispatchedAwaitingPickup;
        return;
      }
      countdownMap.value[Number(key)] = t(`mission.statusBtnMap.countDown`, {
        minute,
        second,
      });
    }
  });
};

const initPage = () => {
  cleanTimer();

  if (missionStore.mateDispatchList.length > 0) {
    countdownMap.value = {};
    missionStore.mateDispatchList.forEach((id) => {
      const mateDispatch = missionStore.getDispatchById(id);
      if (isRunning(mateDispatch)) {
        countdownMap.value[id] = "";
      }
    });
    initCountdown();
  }
  timer = setInterval(() => {
    initCountdown();
  }, 1000);
};

const leavePage = () => {
  cleanTimer();
  countdownMap.value = {};
};

const cleanTimer = () => {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
};

const initHeightMap = () => {
  Object.entries(heightMap.value).forEach(([key, height]) => {
    threadListRefs.value[key]?.scrollTo(height);
  });
};

const onTabChange = (name: string) => {
  if (name === MateDispatchStatusRequest.Returned) {
    notificationStore.initSummaryCount();
  }
  reload();
};

onUnmounted(() => {
  leavePage();
});

onMounted(async () => {
  initPage();
});

onActivated(() => {
  initPage();
  initHeightMap();

  if (sessionStorageGet("mission-home:reload")) {
    reload();
    sessionStorageRemove("mission-home:reload");
  }
});

onDeactivated(() => {
  leavePage();
});

const createTestSession = async () => {
  const mateStore = useMate();
  const sampleMate = mateStore.list[0];

  await createPrivateSession(sampleMate.id, sampleMate.uuid, "push");
};
</script>

<style>
.mission-home-page .van-tabs__content {
  overflow: hidden;
  height: 100%;
}

.mission-home-page .van-tab__panel {
  overflow: hidden;
}

.new-mission-card::-webkit-scrollbar {
  display: none;
}
</style>
